// Single source of truth for broker synchronization values (ADR-SA-20).

import type { ChipVariant } from "../types/smart-table";

export const BROKER_SYNC_PROVIDER = ["ibkr", "tbank", "finam"] as const;
export type TBrokerSyncProvider = (typeof BROKER_SYNC_PROVIDER)[number];
export const BROKER_SYNC_PROVIDER_OBJ = {
  IBKR: "ibkr",
  TBANK: "tbank",
  FINAM: "finam",
} as const satisfies Record<string, TBrokerSyncProvider>;
export const BROKER_SYNC_PROVIDER_LABELS: Record<
  TBrokerSyncProvider,
  Record<string, string>
> = {
  ibkr: { "en-US": "Interactive Brokers", "ru-RU": "Interactive Brokers" },
  tbank: { "en-US": "T-Invest", "ru-RU": "Т-Инвестиции" },
  finam: { "en-US": "Finam", "ru-RU": "Финам" },
};
export const BROKER_SYNC_PROVIDER_VARIANTS: Record<
  TBrokerSyncProvider,
  ChipVariant
> = {
  ibkr: "default",
  tbank: "secondary",
  finam: "outline",
};

export const BROKER_SYNC_RUN_STATUS = [
  "running",
  "succeeded",
  "failed",
] as const;
export type TBrokerSyncRunStatus = (typeof BROKER_SYNC_RUN_STATUS)[number];
export const BROKER_SYNC_RUN_STATUS_OBJ = {
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const satisfies Record<string, TBrokerSyncRunStatus>;
export const BROKER_SYNC_RUN_STATUS_LABELS: Record<
  TBrokerSyncRunStatus,
  Record<string, string>
> = {
  running: { "en-US": "Running", "ru-RU": "Выполняется" },
  succeeded: { "en-US": "Succeeded", "ru-RU": "Завершено" },
  failed: { "en-US": "Failed", "ru-RU": "Ошибка" },
};
export const BROKER_SYNC_RUN_STATUS_VARIANTS: Record<
  TBrokerSyncRunStatus,
  ChipVariant
> = {
  running: "default",
  succeeded: "emerald",
  failed: "destructive",
};

export const BROKER_SYNC_ISSUE_KIND = [
  "unmatched-instrument",
  "unsupported-operation",
  "invalid-payload",
  "currency-unresolved",
  "disappeared",
  "corporate-action",
  "out-of-scope",
] as const;
export type TBrokerSyncIssueKind = (typeof BROKER_SYNC_ISSUE_KIND)[number];
export const BROKER_SYNC_ISSUE_KIND_OBJ = {
  UNMATCHED_INSTRUMENT: "unmatched-instrument",
  UNSUPPORTED_OPERATION: "unsupported-operation",
  INVALID_PAYLOAD: "invalid-payload",
  CURRENCY_UNRESOLVED: "currency-unresolved",
  DISAPPEARED: "disappeared",
  CORPORATE_ACTION: "corporate-action",
  OUT_OF_SCOPE: "out-of-scope",
} as const satisfies Record<string, TBrokerSyncIssueKind>;
export const BROKER_SYNC_ISSUE_KIND_LABELS: Record<
  TBrokerSyncIssueKind,
  Record<string, string>
> = {
  "unmatched-instrument": { "en-US": "Unmatched instrument", "ru-RU": "Инструмент не сопоставлен" },
  "unsupported-operation": { "en-US": "Unsupported operation", "ru-RU": "Неподдерживаемая операция" },
  "invalid-payload": { "en-US": "Invalid data", "ru-RU": "Некорректные данные" },
  "currency-unresolved": { "en-US": "Currency not resolved", "ru-RU": "Не удалось определить валюту" },
  disappeared: { "en-US": "Operation disappeared", "ru-RU": "Операция исчезла у брокера" },
  "corporate-action": { "en-US": "Corporate action", "ru-RU": "Корпоративное действие" },
  "out-of-scope": { "en-US": "Outside supported scope", "ru-RU": "Вне поддерживаемого охвата" },
};
export const BROKER_SYNC_ISSUE_KIND_VARIANTS: Record<
  TBrokerSyncIssueKind,
  ChipVariant
> = {
  "unmatched-instrument": "destructive",
  "unsupported-operation": "destructive",
  "invalid-payload": "destructive",
  "currency-unresolved": "destructive",
  disappeared: "destructive",
  "corporate-action": "secondary",
  "out-of-scope": "outline",
};

export const BROKER_SYNC_ISSUE_STATUS = [
  "open",
  "resolved",
  "ignored",
] as const;
export type TBrokerSyncIssueStatus = (typeof BROKER_SYNC_ISSUE_STATUS)[number];
export const BROKER_SYNC_ISSUE_STATUS_OBJ = {
  OPEN: "open",
  RESOLVED: "resolved",
  IGNORED: "ignored",
} as const satisfies Record<string, TBrokerSyncIssueStatus>;
export const BROKER_SYNC_ISSUE_STATUS_LABELS: Record<
  TBrokerSyncIssueStatus,
  Record<string, string>
> = {
  open: { "en-US": "Open", "ru-RU": "Открыта" },
  resolved: { "en-US": "Resolved", "ru-RU": "Решена" },
  ignored: { "en-US": "Ignored", "ru-RU": "Пропущена" },
};
export const BROKER_SYNC_ISSUE_STATUS_VARIANTS: Record<
  TBrokerSyncIssueStatus,
  ChipVariant
> = {
  open: "destructive",
  resolved: "emerald",
  ignored: "secondary",
};

export const BROKER_SYNC_SECRET_MASK = "****";

export const BROKER_SYNC_DIVIDEND_AMOUNT_BASIS = ["gross", "net"] as const;
export type TBrokerSyncDividendAmountBasis = (typeof BROKER_SYNC_DIVIDEND_AMOUNT_BASIS)[number];
export const BROKER_SYNC_DIVIDEND_AMOUNT_BASIS_OBJ = { GROSS: "gross", NET: "net" } as const;
/** Provider amount parsing distinguishes a real zero from an unavailable sign. */
export const BROKER_SYNC_AMOUNT_SIGN_OBJ = { POSITIVE: "positive", NEGATIVE: "negative", ZERO: "zero", UNKNOWN: "unknown", ANY: "any" } as const;

export const BROKER_SYNC_OPERATION_KIND = ["trade", "income", "cashflow", "currency-exchange", "corporate-action", "out-of-scope", "unsupported"] as const;
export type TBrokerSyncOperationKind = (typeof BROKER_SYNC_OPERATION_KIND)[number];
export const BROKER_SYNC_OPERATION_KIND_OBJ = {
  TRADE: "trade", INCOME: "income", CASHFLOW: "cashflow", CURRENCY_EXCHANGE: "currency-exchange",
  CORPORATE_ACTION: "corporate-action", OUT_OF_SCOPE: "out-of-scope", UNSUPPORTED: "unsupported",
} as const satisfies Record<string, TBrokerSyncOperationKind>;

export const BROKER_SYNC_CASHFLOW_REASON = [
  "deposit",
  "withdrawal",
  "commission",
  "tax",
  "other",
] as const;
export type TBrokerSyncCashflowReason = (typeof BROKER_SYNC_CASHFLOW_REASON)[number];
export const BROKER_SYNC_CASHFLOW_REASON_OBJ = { DEPOSIT: "deposit", WITHDRAWAL: "withdrawal", COMMISSION: "commission", TAX: "tax", OTHER: "other" } as const;

export const BROKER_SYNC_CORPORATE_ACTION = ["split", "conversion", "other"] as const;
export type TBrokerSyncCorporateAction = (typeof BROKER_SYNC_CORPORATE_ACTION)[number];
export const BROKER_SYNC_CORPORATE_ACTION_OBJ = { SPLIT: "split", CONVERSION: "conversion", OTHER: "other" } as const;

export const BROKER_SYNC_OUT_OF_SCOPE_REASON = ["bond", "derivative", "repo", "margin"] as const;
export type TBrokerSyncOutOfScopeReason = (typeof BROKER_SYNC_OUT_OF_SCOPE_REASON)[number];
export const BROKER_SYNC_OUT_OF_SCOPE_REASON_OBJ = { BOND: "bond", DERIVATIVE: "derivative", REPO: "repo", MARGIN: "margin" } as const;

export const BROKER_SYNC_DIVIDEND_DATA_PROVIDER = ["fmp", "moex"] as const;
export type TBrokerSyncDividendDataProvider = (typeof BROKER_SYNC_DIVIDEND_DATA_PROVIDER)[number];
export const BROKER_SYNC_DIVIDEND_DATA_PROVIDER_OBJ = { FMP: "fmp", MOEX: "moex" } as const;

export const BROKER_SYNC_DIVIDEND_DATA_STATUS = ["available", "unavailable"] as const;
export type TBrokerSyncDividendDataStatus = (typeof BROKER_SYNC_DIVIDEND_DATA_STATUS)[number];
export const BROKER_SYNC_DIVIDEND_DATA_STATUS_OBJ = { AVAILABLE: "available", UNAVAILABLE: "unavailable" } as const;


/** `GET /api/portfolio/broker-sync/settings` read state: stored settings are readable (`ok`) or need repair (`invalid`). */
export const BROKER_SYNC_SETTINGS_STATUS = ["ok", "invalid"] as const;
export type TBrokerSyncSettingsStatus = (typeof BROKER_SYNC_SETTINGS_STATUS)[number];
export const BROKER_SYNC_SETTINGS_STATUS_OBJ = {
  OK: "ok",
  INVALID: "invalid",
} as const satisfies Record<string, TBrokerSyncSettingsStatus>;

export const BROKER_SYNC_DEFAULT_ENABLED = false;
export const BROKER_SYNC_DEFAULT_OVERLAP_DAYS = 7;
export const BROKER_SYNC_OVERLAP_DAYS_MIN = 6;
export const BROKER_SYNC_OVERLAP_DAYS_MAX = 30;
export const BROKER_SYNC_DEFAULT_DEEP_WINDOW_DAYS = 90;
export const BROKER_SYNC_DEEP_WINDOW_DAYS_MIN = 30;
export const BROKER_SYNC_DEEP_WINDOW_DAYS_MAX = 365;
